import { useCallback, useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
export const ADMIN_TOKEN_KEY = "np_admin_token";

export const getAdminToken = () => {
  try { return sessionStorage.getItem(ADMIN_TOKEN_KEY) || ""; } catch { return ""; }
};
export const adminAuthHeader = () => {
  const t = getAdminToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/**
 * Centralised admin auth state machine.
 * - autoVerify: hit /admin/me on mount with stored token; if OK, mark authed.
 * - login(password): POST /admin/login, persist token, return success bool.
 * - logout(): clear token + state.
 */
export default function useAdminAuth({ autoVerify = true } = {}) {
  const [authed, setAuthed] = useState(false);
  const [verifying, setVerifying] = useState(autoVerify);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!autoVerify) return;
    const t = getAdminToken();
    if (!t) { setVerifying(false); return; }
    let cancelled = false;
    (async () => {
      try {
        await axios.get(`${API}/admin/me`, { headers: { Authorization: `Bearer ${t}` } });
        if (!cancelled) setAuthed(true);
      } catch {
        try { sessionStorage.removeItem(ADMIN_TOKEN_KEY); } catch { /* ignore */ }
      } finally {
        if (!cancelled) setVerifying(false);
      }
    })();
    return () => { cancelled = true; };
  }, [autoVerify]);

  const login = useCallback(async (password) => {
    setBusy(true); setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem(ADMIN_TOKEN_KEY, data.access_token);
      setAuthed(true);
      return true;
    } catch (e) {
      const detail = e?.response?.data?.detail || "Login fallito";
      setError(typeof detail === "string" ? detail : "Errore");
      setAuthed(false);
      return false;
    } finally { setBusy(false); }
  }, []);

  const logout = useCallback(() => {
    try { sessionStorage.removeItem(ADMIN_TOKEN_KEY); } catch { /* ignore */ }
    setAuthed(false);
    setError("");
  }, []);

  const expireSession = useCallback(() => {
    try { sessionStorage.removeItem(ADMIN_TOKEN_KEY); } catch { /* ignore */ }
    setAuthed(false);
  }, []);

  return { authed, verifying, error, busy, login, logout, expireSession, API };
}
